import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Home, Clock, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { BrandLogo } from "../components/BrandLogo";
import { OrderProgress, buildSteps } from "../components/OrderProgress";
import ZellePayBlock from "../components/ZellePayBlock";
import { api, money } from "../lib/api";
import { track } from "../lib/track";
import { useT } from "../lib/i18n";
import { clearDraft } from "../lib/wizardStore";

export default function Success() {
  const { t } = useT();
  const loc = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");
  const zelleOrder = params.get("zelle") ? params.get("order") : null;
  const [state, setState] = useState("checking");
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (zelleOrder) {
      clearDraft();
      api.get(`/orders/public/${zelleOrder}`).then((r) => { setData(r.data); setState("zelle"); }).catch(() => setState("error"));
      return;
    }
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        if (r.data.payment_status === "paid") {
          clearDraft();
          const s = await api.get(`/orders/summary?session_id=${sessionId}`);
          setData(s.data); setState("paid");
          track("purchase_completed", { meta: { order_number: s.data?.order_number } });
          return;
        }
        tries.current += 1;
        if (tries.current > 12) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        tries.current += 1;
        if (tries.current > 12) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, zelleOrder]);

  return (
    <div className="min-h-dvh hero-wash">
      <div className="mx-auto max-w-lg px-4 py-10">
        <BrandLogo size={40} withText />
        <div className="mt-6 rounded-[1.2rem] border bg-card p-6 shadow-[var(--shadow-soft)]">
          {state === "checking" && (
            <div className="flex flex-col items-center py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "hsl(var(--brand-rose))" }} />
              <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Confirming your payment...</p>
            </div>
          )}
          {state === "paid" && data && (
            <div data-testid="order-confirmation">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-9 w-9" style={{ color: "hsl(var(--success))" }} />
                <div>
                  <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Order confirmed!</h1>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">We’ve received your order, {data.customer.name}.</p>
                </div>
              </div>
              <div className="mt-5 rounded-xl border bg-[hsl(var(--muted))] p-4">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Order number</div>
                <div className="font-display text-xl font-semibold" data-testid="order-confirmation-number">{data.order_number}</div>
              </div>
              <dl className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Kit</dt><dd>{data.kit_qty} magnets</dd></div>
                {(data.add_ons || []).map((ao) => (
                  <div key={ao.id} className="flex justify-between" data-testid="confirm-addon"><dt className="text-[hsl(var(--muted-foreground))]">{ao.title} ×{ao.qty}</dt><dd>{money(ao.line_total_cents)}</dd></div>
                ))}
                {data.amounts.product_discount_cents > 0 && (
                  <div className="flex justify-between" data-testid="confirm-discount"><dt style={{ color: "hsl(var(--success))" }}>{data.amounts.discount_pct || 10}% first-order discount</dt><dd style={{ color: "hsl(var(--success))" }}>−{money(data.amounts.product_discount_cents)}</dd></div>
                )}
                <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">Total paid</dt><dd className="font-semibold">{money(data.amounts.total_cents)}</dd></div>
              </dl>
              <p className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">Order updates will be sent to <b>{data.customer?.email}</b>.</p>
              <div className="mt-5">
                <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>What happens next</div>
                <OrderProgress steps={buildSteps({ payment_method: data.payment_method, payment_status: "paid" })} />
              </div>
              {data.access_token && (
                <>
                  <p className="mt-5 text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Now add your photos</p>
                  <a href={`/order/${data.access_token}/photos`}><Button className="mt-2 h-12 w-full rounded-xl text-base" data-testid="upload-photos-button">Upload your photos</Button></a>
                  <a href={`/track/${data.access_token}`}><Button variant="outline" className="mt-2 h-11 w-full rounded-xl" data-testid="track-order-button"><MapPin className="mr-1.5 h-4 w-4" /> I'll do this later</Button></a>
                  <p className="mt-2 text-center text-[11px] text-[hsl(var(--muted-foreground))]">We also emailed you a secure link to add photos anytime.</p>
                </>
              )}
              <Button onClick={() => navigate("/")} variant="ghost" className="mt-2 h-10 w-full rounded-xl text-sm"><Home className="mr-1.5 h-4 w-4" /> Back to home</Button>
            </div>
          )}
          {state === "zelle" && data && (
            <div data-testid="order-confirmation">
              <div className="flex items-center gap-3">
                <Clock className="h-9 w-9" style={{ color: "hsl(var(--warning))" }} />
                <div>
                  <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("order_received_msg")}</h1>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("zelle_awaiting_msg")}</p>
                </div>
              </div>
              <div className="mt-5 rounded-xl border bg-[hsl(var(--muted))] p-4">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Order number</div>
                <div className="font-display text-xl font-semibold" data-testid="order-confirmation-number">{data.order_number}</div>
              </div>
              <div className="mt-4">
                <ZellePayBlock zelle={data.zelle} amountCents={data.amounts.total_cents} orderNumber={data.order_number} />
              </div>
              <div className="mt-5">
                <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Order status</div>
                <OrderProgress steps={buildSteps({ payment_method: "zelle", payment_status: data.payment_status })} />
              </div>
              {data.access_token && (
                <>
                  <p className="mt-5 text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Now add your photos</p>
                  <a href={`/order/${data.access_token}/photos`}><Button className="mt-2 h-12 w-full rounded-xl text-base" data-testid="upload-photos-button">Upload your photos</Button></a>
                  <a href={`/track/${data.access_token}`}><Button variant="outline" className="mt-2 h-11 w-full rounded-xl" data-testid="track-order-button"><MapPin className="mr-1.5 h-4 w-4" /> I'll do this later</Button></a>
                  <p className="mt-2 text-center text-[11px] text-[hsl(var(--muted-foreground))]">We also emailed you a secure link to add photos anytime.</p>
                </>
              )}
              <Button onClick={() => navigate("/")} variant="ghost" className="mt-2 h-10 w-full rounded-xl text-sm"><Home className="mr-1.5 h-4 w-4" /> Back to home</Button>
            </div>
          )}
          {state === "pending" && (
            <div className="py-6 text-center">
              <h1 className="font-display text-xl font-semibold">We’re confirming your payment</h1>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">This may take a few moments.</p>
              <Button onClick={() => window.location.reload()} variant="outline" className="mt-4 h-11 rounded-xl">Refresh</Button>
            </div>
          )}
          {state === "error" && (
            <div className="py-6 text-center">
              <h1 className="font-display text-xl font-semibold">We couldn’t find this order</h1>
              <Button onClick={() => navigate("/")} className="mt-4 h-11 rounded-xl">Back to home</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
